// context/ThemeContext.js
import React, { createContext, useContext, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getFirstCompany } from "../store/reducers/initReducer";

// Asumiendo que tienes acciones para obtener colores del backend

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const dispatch = useDispatch();

  const { currentCompany, loading: companyLoading } = useSelector(state => state.init);

  const [colors, setColors] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Si no hay compañía cargada, la solicitamos
    if (!currentCompany) {
      dispatch(getFirstCompany());
    }
  }, [dispatch, currentCompany]);

  useEffect(() => {
    if (currentCompany && Object.keys(currentCompany).length > 0) {
      // Asumiendo que los colores están en currentCompany.colors
      // Si están en otra propiedad, ajusta esto
      const companyColors = currentCompany.colors || {};
      setColors(companyColors);
      setLoading(false);
    } else if (companyLoading === false && !currentCompany) {
      // Si no hay compañía pero la carga terminó
      setLoading(false);
    }
  }, [currentCompany, companyLoading]);

  // Función principal para obtener colores
  const getColor = (colorKey, defaultColor = "#cccccc") => {
    if (!colorKey) return defaultColor;

    // Si es un código hexadecimal directo
    if (colorKey.startsWith("#")) {
      return colorKey;
    }

    // Buscar en los colores de la compañía
    return colors[colorKey] || defaultColor;
  };

  // Utilidades de estilos
  const getBackground = (colorKey, defaultColor = "#ffffff") => ({
    backgroundColor: getColor(colorKey, defaultColor),
  });

  const getBorder = (colorKey, width = "1px", style = "solid", defaultColor = "#dee2e6") => ({
    border: `${width} ${style} ${getColor(colorKey, defaultColor)}`,
  });

  const getTextColor = (colorKey, defaultColor = "#ffffff") => ({
    color: getColor(colorKey, defaultColor),
  });

  const getButtonStyles = (bgColor, textColor = "white", borderColor, defaultBg = "#007bff") => ({
    backgroundColor: getColor(bgColor, defaultBg),
    color: getColor(textColor, "#ffffff"),
    border: borderColor ? `1px solid ${getColor(borderColor)}` : "none",
    padding: "8px 16px",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    transition: "all 0.3s ease",
  });

  // NUEVAS FUNCIONES PARA MATERIAL-UI
  const getMuiButtonStyle = (color = "primary", variant = "contained") => {
    const baseColor = getColor(
      color,
      color === "primary"
        ? "#007bff"
        : color === "secondary"
        ? "#6c757d"
        : color === "success"
        ? "#28a745"
        : color === "danger"
        ? "#dc3545"
        : color === "warning"
        ? "#ffc107"
        : color === "info"
        ? "#17a2b8"
        : "#007bff"
    );

    const styles = {
      contained: {
        backgroundColor: baseColor,
        color: getColor("white", "#ffffff"),
        "&:hover": {
          backgroundColor: getColor(`${color}Dark`, `${baseColor}dd`),
        },
        "&:disabled": {
          backgroundColor: getColor("surface", "#f8f9fa"),
          color: getColor("textSecondary", "#6c757d"),
        },
      },
      outlined: {
        color: baseColor,
        border: `1px solid ${baseColor}`,
        backgroundColor: "transparent",
        "&:hover": {
          backgroundColor: getColor("hover", "#e9ecef"),
          border: `1px solid ${baseColor}`,
        },
        "&:disabled": {
          color: getColor("textSecondary", "#6c757d"),
          borderColor: getColor("border", "#dee2e6"),
        },
      },
      text: {
        color: baseColor,
        backgroundColor: "transparent",
        "&:hover": {
          backgroundColor: getColor("hover", "#e9ecef"),
        },
      },
    };

    return styles[variant] || styles.contained;
  };

  const getMuiTextStyle = (color = "textSecondary") => ({
    color: getColor(color, "#212529"),
  });

  const getMuiBackground = (color = "surface") => ({
    backgroundColor: getColor(color, "#ffffff"),
  });

  // Función específica para el círculo de color en el Dialog
  const getBackgroundCircle = colorCode => {
    return colorCode || getColor("borderCatProd", "#6c757d");
  };

  const value = {
    colors,
    loading: loading || companyLoading,
    getColor,
    getBackground,
    getBorder,
    getTextColor,
    getButtonStyles,
    // NUEVAS FUNCIONES EXPORTADAS
    getMuiButtonStyle,
    getMuiTextStyle,
    getMuiBackground,
    getBackgroundCircle,
    refreshColors: () => {
      dispatch(getFirstCompany());
    },
    currentCompany, // También exponemos la compañía completa por si la necesitas
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
